if (!customElements.get("shoppable-video")) {
  customElements.define(
    "shoppable-video",
    class ShoppableVideo extends HTMLElement {
      constructor() {
        super();
      }

      bindMethods() {
        this.togglePlayPause = this.togglePlayPause.bind(this);
        this.toggleVideoIcon = this.toggleVideoIcon.bind(this);
        this.playVideo = this.playVideo.bind(this);
        this.updateScrollPosition = this.updateScrollPosition.bind(this);
        this.throttleUpdateScroll = this.throttle(
          this.updateScrollPosition,
          250,
        );
        this.checkDominantProductElement =
          this.checkDominantProductElement.bind(this);
        this.handleScrollAndUpdateVideoPosition =
          this.handleScrollAndUpdateVideoPosition.bind(this);
        this.handleTouchEnd = this.handleTouchEnd.bind(this);
        this.handleTouchStart = this.handleTouchStart.bind(this);
      }

      setupElements() {
        this.productsInfo = JSON.parse(this.dataset.products);
        this.productsInfoArr = Object.entries(this.productsInfo);
        this.video = this.querySelector("video");
        this.productsContainer = this.querySelector(
          ".shoppable-video__products",
        );
        this.controlButton = this.querySelector(
          ".shoppable-video__control-button",
        );
        this.products = this.productsContainer.querySelectorAll(
          ".shoppable-video__product",
        );
        this.emptyProduct = this.productsContainer.querySelector(
          ".shoppable-video__empty-product",
        );
        this.playButtons = this.productsContainer.querySelectorAll(
          ".shoppable-video__rewind-button",
        );
        this.lastScrolledToProductIndex = null;

        this.playButtonsListeners = [];
        this.isTouching = false;
        this.startX = 0;
        this.startY = 0;
        this.scrollLeftStart = 0;
        this.scrollTopStart = 0;
        this.lastScrollLeft = 0;
        this.dominantProductIndex = 0;
        this.scrollTimeout = null;
        this.touchEnded = false;

        this.calculateEmptyProductWidth();
      }

      addEventListeners() {
        this.controlButton.addEventListener("click", this.togglePlayPause);

        this.video?.addEventListener("timeupdate", this.throttleUpdateScroll);
        this.video?.addEventListener("play", this.toggleVideoIcon);
        this.video?.addEventListener("pause", this.toggleVideoIcon);

        this.productsContainer.addEventListener(
          "touchstart",
          this.handleTouchStart,
        );

        this.productsContainer.addEventListener(
          "scroll",
          this.handleScrollAndUpdateVideoPosition,
        );
        this.productsContainer.addEventListener(
          "touchmove",
          this.handleScrollAndUpdateVideoPosition,
        );

        this.productsContainer.addEventListener(
          "touchend",
          this.handleTouchEnd,
        );

        this.playButtons.forEach((btn, index) => {
          const boundClickRewindButton = this.clickRewindButton.bind(this, btn);
          btn.addEventListener("click", boundClickRewindButton);
          // Store the bound function for later removal
          this.playButtonsListeners.push({ btn, boundClickRewindButton });
        });
      }

      handleTouchStart(e) {
        this.video?.pause();
        this.isTouching = true;
        this.startX = e.touches[0].pageX;
        this.startY = e.touches[0].pageY;
        this.scrollLeftStart = this.productsContainer.scrollLeft;
        this.scrollTopStart = this.productsContainer.scrollTop;
      }

      handleTouchEnd() {
        this.touchEnded = true;
      }

      handleScrollAndUpdateVideoPosition() {
        // Clear any existing timeout
        if (this.scrollTimeout) clearTimeout(this.scrollTimeout);
        
        // Use requestAnimationFrame to batch DOM reads
        requestAnimationFrame(() => {
          // Read phase - gather all measurements at once
          const currentScrollLeft = this.productsContainer.scrollLeft;
          const scrolledRight = currentScrollLeft > this.lastScrollLeft;
          const scrolledLeft = currentScrollLeft < this.lastScrollLeft;
          
          // Update lastScrollLeft for the next scroll event
          this.lastScrollLeft = currentScrollLeft;
          
          // Check which product element is dominant
          this.checkDominantProductElement(scrolledRight, scrolledLeft);
          
          // Early return if not touching
          if (!this.isTouching) return;
          
          // Set timeout to update video position
          this.scrollTimeout = setTimeout(() => {
            // Use another requestAnimationFrame to batch video updates
            requestAnimationFrame(() => {
              const startSecond = this.productsInfoArr[this.dominantProductIndex][1];
              this.lastScrolledToProductIndex = this.dominantProductIndex - 1;
              
              if (this.video) {
                this.video.currentTime = startSecond;
                this.video.play();
              }
            });
          }, 250);
        });
      }

      checkDominantProductElement() {
        // Use requestAnimationFrame to ensure all DOM reads happen in the same frame
        requestAnimationFrame(() => {
          // Read phase - gather all measurements at once
          let dominantProductIndex = -1;
          let maxVisibleWidth = -1;
          
          // Cache container rect to avoid multiple reads
          const containerRect = this.productsContainer.getBoundingClientRect();
          const containerWidth = this.productsContainer.offsetWidth;
          
          // Batch read all product positions
          const productVisibilities = Array.from(this.products).map((product, index) => {
            const { left, right } = product.getBoundingClientRect();
            
            // Calculate visibility
            const visibleWidth = Math.min(containerRect.right, right) - 
                                Math.max(containerRect.left, left);
            
            return { index, visibleWidth };
          });
          
          // Process the collected data without triggering reflows
          productVisibilities.forEach(({ index, visibleWidth }) => {
            if (visibleWidth > maxVisibleWidth) {
              maxVisibleWidth = visibleWidth;
              dominantProductIndex = index;
            }
          });
          
          // Update state based on calculations
          if (maxVisibleWidth / containerWidth > 0.2) {
            this.dominantProductIndex = dominantProductIndex;
          }
        });
      }

      calculateEmptyProductWidth() {
        // Check if emptyProduct element exists
        if (!this.emptyProduct) {
          return;
        }
        
        // Use requestAnimationFrame to separate read and write operations
        requestAnimationFrame(() => {
          // Additional safety check in case element was removed
          if (!this.emptyProduct || !this.products || !this.products[1]) {
            return;
          }
          
          // Read phase - gather all measurements at once
          const firstProductWidth = this.products[1].offsetWidth;
          this.gap = parseFloat(
            window
              .getComputedStyle(this.productsContainer)
              .getPropertyValue("gap")
          );
          
          // Write phase - in next frame to avoid forced reflow
          requestAnimationFrame(() => {
            // Final safety check before DOM manipulation
            if (this.emptyProduct && this.emptyProduct.style) {
              this.emptyProduct.style.width = `calc(100% - ${firstProductWidth}px - ${this.gap}px)`;
            }
          });
        });
      }

      setContainerMargin() {
        // Use requestAnimationFrame to separate read and write operations
        requestAnimationFrame(() => {
          // Read phase - gather all measurements at once
          let maxHeight = 0;
          let elementWithMaxHeight = null;
          
          // Cache window.innerWidth to avoid multiple reads
          const windowWidth = window.innerWidth;
          
          // Batch read all element heights
          const elementHeights = Array.from(this.products).map(element => {
            const height = element.offsetHeight;
            if (height > maxHeight) {
              maxHeight = height;
              elementWithMaxHeight = element;
            }
            return height;
          });
          
          const marginValue = windowWidth > 900 ? 46 : 34;
          const finalMarginTop = `-${maxHeight + marginValue}px`;
          
          // Write phase - in next frame to avoid forced reflow
          requestAnimationFrame(() => {
            this.productsContainer.style.marginTop = finalMarginTop;
          });
        });
      }

      toggleVideoIcon() {
        // Read phase - determine state
        const isPlaying = !this.video.paused;
        
        // Use requestAnimationFrame to batch DOM updates
        requestAnimationFrame(() => {
          // Write phase - update classes
          this.controlButton.classList.toggle(
            "shoppable-video__control-button--play",
            isPlaying
          );
          this.controlButton.classList.toggle(
            "shoppable-video__control-button--pause",
            !isPlaying
          );
        });
      }

      removeEventListeners() {
        this.controlButton.removeEventListener("click", this.togglePlayPause);

        this.video?.removeEventListener("click", this.togglePlayPause);
        this.video?.removeEventListener(
          "timeupdate",
          this.throttleUpdateScroll,
        );
        this.video?.removeEventListener("play", this.toggleVideoIcon);
        this.video?.removeEventListener("pause", this.toggleVideoIcon);

        this.productsContainer.removeEventListener(
          "touchstart",
          this.handleTouchStart,
        );

        this.productsContainer.removeEventListener(
          "scroll",
          this.handleScrollAndUpdateVideoPosition,
        );
        this.productsContainer.removeEventListener(
          "touchend",
          this.handleTouchEnd,
        );

        this.playButtonsListeners.forEach(({ btn, boundClickPlayButton }) => {
          btn.removeEventListener("click", boundClickPlayButton);
        });
      }

      clickRewindButton(btn) {
        const productIndex = btn.dataset.productIndex;
        const startSecond = this.productsInfo[productIndex];
        this.video.currentTime = startSecond;
        this.playVideo();
        this.animateScrollToNextProduct(productIndex);
      }

      animateScrollToNextProduct(productIndex) {
        // Use requestAnimationFrame to batch DOM reads
        requestAnimationFrame(() => {
          // Read phase - gather all measurements at once
          const productWidth = this.products[productIndex]?.offsetWidth;
          const scrollWidth = this.productsContainer.scrollWidth;
          const clientWidth = this.productsContainer.clientWidth;
          const startPosition = this.productsContainer.scrollLeft;
          
          // Calculate scroll position without triggering reflows
          const nextScrollPosition =
            (Number(this.productsInfoArr[productIndex][0]) + 1) *
            (productWidth + this.gap);
          
          const maxScrollLeft = scrollWidth - clientWidth;
          const clampedNextScrollPosition = Math.min(
            nextScrollPosition,
            maxScrollLeft
          );
          
          // Store the index for future reference
          this.lastScrolledToProductIndex = productIndex;
          
          // Perform the animation in the next frame
          const duration = 250; // duration in milliseconds
          this.animateScroll(startPosition, clampedNextScrollPosition, duration);
        });
      }

      togglePlayPause(e) {
        e.preventDefault(); // Prevent default click behavior
        const videoElement = this.video; // Ensure this is your video element
        if (!videoElement) return; // Exit if videoElement is not defined

        // Toggle play/pause based on the current state
        if (videoElement.paused || videoElement.ended) {
          videoElement.play();
        } else {
          videoElement.pause();
        }
      }

      playVideo() {
        const videoElement = this.video; // Ensure this is your video element
        if (!videoElement) return; // Exit if videoElement is not defined

        // Toggle play/pause based on the current state
        if (videoElement.paused || videoElement.ended) {
          videoElement.play();
        }
      }

      updateScrollPosition() {
        // Use requestAnimationFrame to batch DOM reads and calculations
        requestAnimationFrame(() => {
          // Read phase - gather all measurements at once
          const currentTime = this.video.currentTime;
          const windowWidth = window.innerWidth;
          const isMobile = windowWidth <= 400;
          const currentScrollLeft = this.productsContainer.scrollLeft;
          const containerWidth = this.productsContainer.offsetWidth;
          const containerScrollWidth = this.productsContainer.scrollWidth;
          
          // Reset touch state
          this.isTouching = false;
          
          // Prepare product measurements to avoid multiple reads
          const productMeasurements = Array.from(this.products).map(product => ({
            offsetLeft: product.offsetLeft,
            offsetWidth: product.offsetWidth
          }));
          
          // Scroll to start if the video just started
          if (
            currentTime < 1 &&
            currentTime > 0 &&
            this.lastScrolledToProductIndex !== 0
          ) {
            this.lastScrolledToProductIndex = 0;
            
            // Schedule the scroll in next frame
            requestAnimationFrame(() => {
              this.productsContainer.scrollTo({
                left: 0,
                behavior: "smooth",
              });
            });
            return;
          }
          
          // Find the product that matches current video time
          let targetProduct = null;
          let targetScrollPosition = null;
          let targetIndex = null;
          
          for (
            let i = this.lastScrolledToProductIndex || 0;
            i < this.productsInfoArr.length;
            i++
          ) {
            const itemTime = Number(this.productsInfoArr[i][1]);
            const shouldScrollMove =
              currentTime > itemTime && currentTime < itemTime + 1;
              
            if (shouldScrollMove) {
              const productMeasurement = productMeasurements[i];
              let nextScrollPosition;
              
              if (isMobile) {
                // Mobile: target element 10px from the left
                nextScrollPosition = productMeasurement.offsetLeft - 10;
              } else {
                // Desktop: target element 20px from the right
                const productRightOffset =
                  productMeasurement.offsetLeft +
                  productMeasurement.offsetWidth -
                  containerWidth +
                  20;
                nextScrollPosition = productRightOffset;
              }
              
              // Ensure it doesn't go below 0
              nextScrollPosition = Math.max(nextScrollPosition, 0);
              
              // Check if we need to scroll
              if (Math.abs(currentScrollLeft - nextScrollPosition) > 1) {
                targetIndex = i;
                targetScrollPosition = Math.min(
                  nextScrollPosition,
                  containerScrollWidth - containerWidth
                );
                break;
              }
            }
          }
          
          // If we found a target, animate to it in the next frame
          if (targetIndex !== null && targetScrollPosition !== null) {
            this.lastScrolledToProductIndex = targetIndex;
            
            // Schedule the animation in next frame
            requestAnimationFrame(() => {
              const startPosition = this.productsContainer.scrollLeft;
              const duration = 250;
              this.animateScroll(startPosition, targetScrollPosition, duration);
            });
          }
        });
      }

      throttle(func, limit) {
        let inThrottle;
        return function () {
          const args = arguments;
          const context = this;
          if (!inThrottle) {
            func.apply(context, args);
            inThrottle = true;
            setTimeout(() => (inThrottle = false), limit);
          }
        };
      }

      animateScroll(startPosition, endPosition, duration) {
        const start = performance.now();
        const animate = (time) => {
          const timeElapsed = time - start;
          const progress = Math.min(timeElapsed / duration, 1);
          const currentPosition =
            startPosition +
            (endPosition - startPosition) * this.easeOutSine(progress);
          this.productsContainer.scrollLeft = currentPosition;

          if (timeElapsed < duration) {
            requestAnimationFrame(animate);
          } else {
            this.productsContainer.scrollLeft = endPosition;
          }
        };
        requestAnimationFrame(animate);
      }

      easeOutSine(t) {
        return Math.sin((t * Math.PI) / 2);
      }

      connectedCallback() {
        this.setupElements();
        this.bindMethods();

        this.addEventListeners();
      }

      disconnectedCallback() {
        this.removeEventListeners();
      }
    },
  );
}
